/**
 * MarketplaceFAQ Component
 * Comprehensive FAQ section for the Strategy Marketplace
 */
import { useState } from 'react'

interface FAQItem {
  question: string
  answer: string
}

interface FAQSection {
  title: string
  icon: string
  items: FAQItem[]
}

const FAQ_DATA: FAQSection[] = [
  {
    title: 'Getting Started',
    icon: '🚀',
    items: [
      {
        question: 'How do I browse strategies on the marketplace?',
        answer: 'Simply visit the Marketplace page and browse through our collection of trading strategies. You can filter by category (Trend Following, Mean Reversion, Breakout, etc.), pricing model (Free, Paid, Subscription), and performance metrics. Click on any strategy card to view detailed information including performance metrics, reviews, and creator details.'
      },
      {
        question: 'Do I need a verified account to use the marketplace?',
        answer: 'Yes, you need to have a ZO Bull account connected with at least one broker to access the marketplace. This ensures secure strategy cloning and subscription management. If you haven\'t connected a broker yet, head to the Broker Connect page to get started.'
      },
      {
        question: 'Is the marketplace free to use?',
        answer: 'While browsing and comparing strategies is completely free, purchasing or cloning paid strategies requires payment as specified by the strategy creator. Some strategies are completely free, while others offer subscription-based access or one-time purchases.'
      }
    ]
  },
  {
    title: 'Strategy Cloning',
    icon: '📋',
    items: [
      {
        question: 'What does "cloning" a strategy mean?',
        answer: 'Cloning copies the complete strategy configuration to your own ZO Bull account. This includes all indicator settings, entry/exit rules, and parameters. Once cloned, you can backtest it with your preferred settings and deploy it to your connected broker.'
      },
      {
        question: 'How do I clone a free strategy?',
        answer: 'Click on any free strategy to view its details, then click the "Clone Strategy" button. The strategy will be instantly added to your Strategies page where you can customize and backtest it before deployment.'
      },
      {
        question: 'How do I purchase a paid strategy?',
        answer: 'Click on a paid strategy to view its details and pricing. For one-time purchases, you\'ll be directed to complete the payment. For subscription strategies, you\'ll set up a recurring subscription. Once purchased, the strategy is available in your account.'
      },
      {
        question: 'Can I modify a cloned strategy?',
        answer: 'Absolutely! Once cloned, the strategy is yours to modify. You can adjust indicators, change parameters, combine it with other strategies, or use it as a template for new strategies. Your modifications won\'t affect the original.'
      }
    ]
  },
  {
    title: 'Strategy Creation & Publishing',
    icon: '✍️',
    items: [
      {
        question: 'How do I publish my strategy on the marketplace?',
        answer: 'Navigate to the Strategy Lab, create or select your strategy, then click "Publish Strategy". You\'ll go through a wizard to set your strategy details, pricing model, and category. Once submitted, your strategy goes through a quick validation and becomes live on the marketplace.'
      },
      {
        question: 'What pricing models can I use?',
        answer: 'You have four options: Free (anyone can clone at no cost), Paid (one-time payment), Subscription (monthly recurring fee), or Freemium (basic version free, premium features paid). Choose the model that best fits your monetization strategy.'
      },
      {
        question: 'How much does it cost to publish?',
        answer: 'Publishing on the marketplace is completely free! We take a small platform fee (30%) only when you earn revenue from paid strategies. There are no upfront costs or monthly subscriptions required.'
      },
      {
        question: 'What are the requirements for publishing?',
        answer: 'Your strategy must have: a descriptive name (3+ characters), detailed description (20+ characters), at least one indicator configured, and valid backtest results with at least 1 trade. We also recommend adding tags and screenshots to improve visibility.'
      },
      {
        question: 'Can I update my strategy after publishing?',
        answer: 'Yes! You can update your strategy details, description, pricing, and configuration at any time through your Creator Dashboard. Major changes will notify your existing subscribers.'
      }
    ]
  },
  {
    title: 'Earnings & Payments',
    icon: '💰',
    items: [
      {
        question: 'How do I receive payments for my strategies?',
        answer: 'Payments are processed automatically through our platform. For paid strategies, you receive 70% of each sale after platform fees. For subscriptions, you receive monthly payments proportional to active subscribers. Earnings are credited to your connected payout account.'
      },
      {
        question: 'When do I get paid?',
        answer: 'For one-time purchases, you receive payment within 24-48 hours after the transaction completes. For subscriptions, payments are processed at the beginning of each billing cycle and transferred to your account.'
      },
      {
        question: 'What fees does ZO Bull charge?',
        answer: 'ZO Bull charges a 30% platform fee on all marketplace transactions. This covers payment processing, platform maintenance, marketing, and customer support. You keep 70% of all earnings.'
      },
      {
        question: 'How do I track my earnings?',
        answer: 'Your Creator Dashboard shows real-time analytics including total earnings, clone counts, subscription metrics, and revenue by strategy. You can also view detailed transaction history and export reports.'
      }
    ]
  },
  {
    title: 'Reviews & Ratings',
    icon: '⭐',
    items: [
      {
        question: 'How do ratings work?',
        answer: 'After using a strategy, you can rate it from 1 to 5 stars based on your experience. You can also leave a detailed review explaining what you liked or didn\'t like. Ratings help other traders discover quality strategies.'
      },
      {
        question: 'Can I review strategies I haven\'t purchased?',
        answer: 'You can browse and compare strategies without purchasing, but to leave a review, you need to have cloned or subscribed to that strategy. This ensures reviews come from actual users.'
      },
      {
        question: 'Can creators respond to reviews?',
        answer: 'Yes! As a strategy creator, you can respond to reviews from your Creator Dashboard. This helps build trust and allows you to address concerns or thank users for positive feedback.'
      }
    ]
  },
  {
    title: 'Troubleshooting',
    icon: '🔧',
    items: [
      {
        question: 'Why is my strategy not appearing in search results?',
        answer: 'Ensure your strategy is published (not in draft), has proper tags and category, and includes a detailed description. New strategies may take a few hours to fully index. If issues persist, check your strategy status in the Creator Dashboard.'
      },
      {
        question: 'My strategy clone failed. What should I do?',
        answer: 'First, ensure your broker is properly connected. If the issue persists, try refreshing the page and attempting again. For persistent issues, contact support with your strategy ID and error message.'
      },
      {
        question: 'How do I report a problematic strategy?',
        answer: 'If you believe a strategy violates our guidelines (misleading metrics, copyrighted content, etc.), use the "Report" button on the strategy page. Our team reviews all reports within 24 hours.'
      }
    ]
  }
]

interface FAQItemProps {
  question: string
  answer: string
  isOpen: boolean
  onToggle: () => void
}

function FAQItemComponent({ question, answer, isOpen, onToggle }: FAQItemProps) {
  return (
    <div className="faq-item">
      <button
        className={`faq-question ${isOpen ? 'open' : ''}`}
        onClick={onToggle}
      >
        <span>{question}</span>
        <span className="faq-icon">{isOpen ? '−' : '+'}</span>
      </button>
      <div className={`faq-answer ${isOpen ? 'open' : ''}`}>
        <p>{answer}</p>
      </div>
    </div>
  )
}

interface MarketplaceFAQProps {
  defaultOpen?: boolean
  sections?: FAQSection[]
}

export default function MarketplaceFAQ({ 
  defaultOpen = false,
  sections = FAQ_DATA 
}: MarketplaceFAQProps) {
  const [openSections, setOpenSections] = useState<Set<string>>(
    defaultOpen ? new Set(sections.map(s => s.title)) : new Set()
  )
  const [openItems, setOpenItems] = useState<Set<string>>(new Set())

  const toggleSection = (title: string) => {
    setOpenSections(prev => {
      const next = new Set(prev)
      if (next.has(title)) {
        next.delete(title)
      } else {
        next.add(title)
      }
      return next
    })
  }

  const toggleItem = (key: string) => {
    setOpenItems(prev => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  return (
    <div className="marketplace-faq">
      <div className="faq-header">
        <h2>📚 Frequently Asked Questions</h2>
        <p>Everything you need to know about the ZO Bull Strategy Marketplace</p>
      </div>

      <div className="faq-sections">
        {sections.map((section) => (
          <div key={section.title} className="faq-section">
            <button
              className={`faq-section-header ${openSections.has(section.title) ? 'open' : ''}`}
              onClick={() => toggleSection(section.title)}
            >
              <span className="faq-section-icon">{section.icon}</span>
              <span className="faq-section-title">{section.title}</span>
              <span className="faq-section-count">({section.items.length})</span>
              <span className={`faq-chevron ${openSections.has(section.title) ? 'open' : ''}`}>
                ▼
              </span>
            </button>

            {openSections.has(section.title) && (
              <div className="faq-section-content">
                {section.items.map((item, index) => {
                  const key = `${section.title}-${index}`
                  return (
                    <FAQItemComponent
                      key={key}
                      question={item.question}
                      answer={item.answer}
                      isOpen={openItems.has(key)}
                      onToggle={() => toggleItem(key)}
                    />
                  )
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="faq-footer">
        <p>Still have questions?</p>
        <button className="faq-contact-btn">
          📧 Contact Support
        </button>
      </div>
    </div>
  )
}

export { FAQ_DATA }
